import type * as PdfjsLib from 'pdfjs-dist';
import type * as PdfjsViewer from 'pdfjs-dist/web/pdf_viewer.mjs';
import { PDFJS_WORKER_PATH } from '../../vite/pdfjsAssetBase';

export interface PdfjsModules {
  lib: typeof PdfjsLib;
  viewer: typeof PdfjsViewer;
}

let modulePromise: Promise<PdfjsModules> | null = null;
let loadedViewer: typeof PdfjsViewer | null = null;
let loadedLib: typeof PdfjsLib | null = null;

/**
 * Loads all of pdf.js on demand. Everything here is dynamic on purpose: a static import
 * anywhere in the component would put the ~420 KB core and its ~160 KB stylesheet into
 * whichever chunk bundles PdfViewer — the entry chunk in befunde-ui — so every user paid
 * for them on first paint whether or not they ever opened a PDF.
 *
 * The order also matters: `pdf_viewer.mjs` destructures `globalThis.pdfjsLib` at module
 * top level, and static ESM imports are evaluated before the importing module's body, so
 * the global can only be populated between two dynamic imports.
 */
export const loadPdfjs = (): Promise<PdfjsModules> => {
  modulePromise ??= (async () => {
    // Cached only once it succeeds — see the catch below.
    await import('pdfjs-dist/web/pdf_viewer.css');

    // The legacy build polyfills newer builtins (Map.getOrInsertComputed) that the
    // Chromium of desktop clients before 1.9 lacks; the modern build breaks there.
    const lib = await import('pdfjs-dist/legacy/build/pdf.mjs');
    loadedLib = lib;
    Object.assign(globalThis, { pdfjsLib: lib });

    // Served by the pdfjsAssets plugin rather than emitted by Vite, so the file keeps a
    // .js extension instead of the .mjs the package ships — see PDFJS_WORKER_PATH.
    lib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_PATH;

    loadedViewer = await import('pdfjs-dist/legacy/web/pdf_viewer.mjs');
    return { lib, viewer: loadedViewer };
  })().catch(error => {
    // Without this the rejected promise stays cached and every later attempt fails with
    // it, so one flaky chunk request would break the viewer until the page is reloaded.
    modulePromise = null;
    throw error;
  });
  return modulePromise;
};

/** Non-null once `loadPdfjs()` has resolved. */
export const getLoadedPdfjsViewer = (): typeof PdfjsViewer | null => loadedViewer;

/** Non-null once `loadPdfjs()` has resolved. */
export const getLoadedPdfjs = (): typeof PdfjsLib | null => loadedLib;
